import moment from "moment";

const CHANNEL_ORDER = ["Airbnb", "Vrbo", "Direct", "Booking.com", "Other"];
const DAYS_OF_WEEK = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];
const STAY_BUCKETS = ["1-2 Nights", "3-4 Nights", "5-7 Nights", "8+ Nights"];
const LEAD_BUCKETS = ["0-7 Days", "8-30 Days", "31-90 Days", "91+ Days"];

const safeNumber = (value) => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const round = (value, precision = 1) => {
  if (!Number.isFinite(value)) return null;
  const base = 10 ** precision;
  return Math.round(value * base) / base;
};

const sum = (items, selector = (item) => item) =>
  items.reduce((total, item) => total + (selector(item) || 0), 0);

const average = (items, selector = (item) => item) => {
  if (!items.length) return null;
  return sum(items, selector) / items.length;
};

const percentage = (value, total) => {
  if (!total) return null;
  return (value / total) * 100;
};

const getDateDiffInDays = (start, end) => {
  const a = moment(start).startOf("day");
  const b = moment(end).startOf("day");
  const days = b.diff(a, "days");
  return days > 0 ? days : 1;
};

const getStayBucket = (nights) => {
  if (nights <= 2) return "1-2 Nights";
  if (nights <= 4) return "3-4 Nights";
  if (nights <= 7) return "5-7 Nights";
  return "8+ Nights";
};

const getLeadBucket = (leadTime) => {
  if (leadTime <= 7) return "0-7 Days";
  if (leadTime <= 30) return "8-30 Days";
  if (leadTime <= 90) return "31-90 Days";
  return "91+ Days";
};

const getSeason = (date) => {
  const month = moment(date).month() + 1;
  if ([12, 1, 2].includes(month)) return "Winter";
  if ([3, 4, 5].includes(month)) return "Spring";
  if ([6, 7, 8].includes(month)) return "Summer";
  return "Fall";
};

const normalizeChannel = (formattedSource, rawSource) => {
  const candidate = String(formattedSource || rawSource || "").trim();
  const lowered = candidate.toLowerCase();
  if (!candidate) return "Other";
  if (lowered.includes("airbnb")) return "Airbnb";
  if (lowered.includes("vrbo") || lowered.includes("homeaway")) return "Vrbo";
  if (lowered.includes("booking")) return "Booking.com";
  if (
    lowered.includes("direct") ||
    lowered.includes("website") ||
    lowered.includes("web site") ||
    lowered.includes("brand")
  ) {
    return "Direct";
  }
  if (CHANNEL_ORDER.includes(candidate)) return candidate;
  return "Other";
};

const sortMetricRows = (rows, key = "value") =>
  [...rows].sort((a, b) => (b[key] || 0) - (a[key] || 0));

const groupRows = (items, groupBy, seedFactory = () => ({}), reducer) => {
  const map = new Map();
  items.forEach((item) => {
    const key = groupBy(item);
    const existing = map.get(key) || { key, ...seedFactory(item) };
    map.set(key, reducer(existing, item));
  });
  return [...map.values()];
};

const addShare = (rows, valueKey = "value") => {
  const total = sum(rows, (row) => row[valueKey]);
  return rows.map((row) => ({
    ...row,
    share: percentage(row[valueKey], total),
  }));
};

const toTableMetric = (label, value, extra = {}) => ({ label, value, ...extra });

const buildGuestKey = (row) => {
  if (row.guest_email) return `email:${String(row.guest_email).trim().toLowerCase()}`;
  if (row.guest_phone) return `phone:${String(row.guest_phone).trim()}`;
  return `name:${String(row.guest_name || "unknown").trim().toLowerCase()}`;
};

const formatMonthKey = (date) => moment(date).format("YYYY-MM");
const formatYearKey = (date) => moment(date).format("YYYY");

const computeGrowthRows = (rows, segmentKey, valueKey) => {
  if (rows.length < 2) return [];
  const sorted = [...rows].sort((a, b) => moment(a.check_in).valueOf() - moment(b.check_in).valueOf());
  const midpoint = Math.floor(sorted.length / 2);
  const firstHalf = sorted.slice(0, midpoint);
  const secondHalf = sorted.slice(midpoint);
  const segments = new Map();

  firstHalf.forEach((row) => {
    const key = segmentKey(row);
    const existing = segments.get(key) || { label: key, first: 0, second: 0 };
    existing.first += row[valueKey] || 0;
    segments.set(key, existing);
  });

  secondHalf.forEach((row) => {
    const key = segmentKey(row);
    const existing = segments.get(key) || { label: key, first: 0, second: 0 };
    existing.second += row[valueKey] || 0;
    segments.set(key, existing);
  });

  return [...segments.values()]
    .map((row) => ({
      ...row,
      absoluteGrowth: row.second - row.first,
      growthRate: row.first > 0 ? ((row.second - row.first) / row.first) * 100 : null,
    }))
    .filter((row) => row.absoluteGrowth > 0)
    .sort((a, b) => (b.absoluteGrowth || 0) - (a.absoluteGrowth || 0));
};

const buildOpportunity = ({
  name,
  impact,
  confidence,
  reasoning,
  action,
}) => ({
  opportunityName: name,
  estimatedAnnualImpact: round(impact || 0, 0),
  confidenceLevel: confidence,
  reasoning,
  recommendedAction: action,
});

export function buildExecutiveAnalyticsReport(rows = [], options = {}) {
  const startDate = options.startDate || moment().subtract(29, "days").format("YYYY-MM-DD");
  const endDate = options.endDate || moment().format("YYYY-MM-DD");
  const daysInRange = Math.max(getDateDiffInDays(startDate, endDate), 1);
  const annualizationFactor = 365 / daysInRange;

  const normalized = rows.map((row) => {
    const nights = getDateDiffInDays(row.check_in, row.check_out);
    const leadTime = Math.max(moment(row.check_in).startOf("day").diff(moment(row.created_at).startOf("day"), "days"), 0);
    const revenue = safeNumber(row.rent_total);
    const isCancelled = row.category === "CXL";
    return {
      guestReservationId: row.guest_reservation_id,
      reservationId: row.reservation_id,
      rentalId: row.rental_id || 0,
      propertyName: row.rental_name || "Unknown Property",
      propertyLocation: row.location_city || row.destination_name || "Unknown",
      channel: normalizeChannel(row.formatted_source, row.raw_source),
      rawSource: row.raw_source || "",
      revenue,
      hasRevenue: revenue !== null,
      guestName: row.guest_name || [row.first_name, row.last_name].filter(Boolean).join(" "),
      guestEmail: row.guest_email || "",
      guestPhone: row.guest_phone || "",
      guestKey: buildGuestKey({
        guest_email: row.guest_email,
        guest_phone: row.guest_phone,
        guest_name: row.guest_name,
      }),
      arrivalDate: row.check_in,
      departureDate: row.check_out,
      createdAt: row.created_at,
      status: row.status,
      category: row.category,
      isCancelled,
      nights,
      leadTime,
      adr: revenue !== null && nights > 0 ? revenue / nights : null,
      monthKey: formatMonthKey(row.check_in),
      yearKey: formatYearKey(row.check_in),
      dayOfWeek: moment(row.check_in).format("dddd"),
      season: getSeason(row.check_in),
      stayBucket: getStayBucket(nights),
      leadBucket: getLeadBucket(leadTime),
    };
  });

  const standardReservations = normalized.filter((row) => !row.isCancelled);
  const cancelledReservations = normalized.filter((row) => row.isCancelled);
  const revenueReservations = standardReservations.filter((row) => row.hasRevenue);

  const guestGroups = new Map();
  standardReservations.forEach((row) => {
    const current = guestGroups.get(row.guestKey) || [];
    current.push(row);
    guestGroups.set(row.guestKey, current);
  });

  const repeatGuestKeys = new Set(
    [...guestGroups.entries()]
      .filter(([, guestRows]) => guestRows.length > 1)
      .map(([guestKey]) => guestKey),
  );

  const standardReservationsWithRepeat = standardReservations.map((row) => ({
    ...row,
    isRepeatGuest: repeatGuestKeys.has(row.guestKey),
  }));
  const revenueReservationsWithRepeat = standardReservationsWithRepeat.filter((row) => row.hasRevenue);

  const revenueByMonth = addShare(sortMetricRows(groupRows(
    revenueReservations,
    (row) => row.monthKey,
    (row) => ({ label: row.monthKey, revenue: 0, reservations: 0, nights: 0 }),
    (bucket, row) => ({
      ...bucket,
      revenue: bucket.revenue + (row.revenue || 0),
      reservations: bucket.reservations + 1,
      nights: bucket.nights + row.nights,
    }),
  ).map((row) => ({
    label: row.label,
    value: round(row.revenue, 2),
    reservations: row.reservations,
    adr: row.nights > 0 ? round(row.revenue / row.nights, 2) : null,
  }))), "value").reverse();

  const revenueByYear = addShare(sortMetricRows(groupRows(
    revenueReservations,
    (row) => row.yearKey,
    (row) => ({ label: row.yearKey, revenue: 0 }),
    (bucket, row) => ({ ...bucket, revenue: bucket.revenue + (row.revenue || 0) }),
  ).map((row) => ({ label: row.label, value: round(row.revenue, 2) }))));

  const revenueByProperty = addShare(sortMetricRows(groupRows(
    revenueReservationsWithRepeat,
    (row) => row.propertyName,
    (row) => ({ label: row.propertyName, revenue: 0, reservations: 0, nights: 0, repeatReservations: 0, leadTime: 0 }),
    (bucket, row) => ({
      ...bucket,
      revenue: bucket.revenue + (row.revenue || 0),
      reservations: bucket.reservations + 1,
      nights: bucket.nights + row.nights,
      repeatReservations: bucket.repeatReservations + (row.isRepeatGuest ? 1 : 0),
      leadTime: bucket.leadTime + row.leadTime,
    }),
  ).map((row) => ({
    label: row.label,
    value: round(row.revenue, 2),
    reservationCount: row.reservations,
    adr: row.nights ? round(row.revenue / row.nights, 2) : null,
    averageStayLength: row.reservations ? round(row.nights / row.reservations, 1) : null,
    averageLeadTime: row.reservations ? round(row.leadTime / row.reservations, 1) : null,
    repeatGuestRate: row.reservations ? round((row.repeatReservations / row.reservations) * 100, 1) : null,
  }))));

  const revenueByChannel = addShare(sortMetricRows(groupRows(
    revenueReservationsWithRepeat,
    (row) => row.channel,
    (row) => ({ label: row.channel, revenue: 0, reservations: 0, nights: 0, leadTime: 0, repeatReservations: 0 }),
    (bucket, row) => ({
      ...bucket,
      revenue: bucket.revenue + (row.revenue || 0),
      reservations: bucket.reservations + 1,
      nights: bucket.nights + row.nights,
      leadTime: bucket.leadTime + row.leadTime,
      repeatReservations: bucket.repeatReservations + (row.isRepeatGuest ? 1 : 0),
    }),
  ).map((row) => ({
    label: row.label,
    value: round(row.revenue, 2),
    reservationCount: row.reservations,
    adr: row.nights ? round(row.revenue / row.nights, 2) : null,
    averageStayLength: row.reservations ? round(row.nights / row.reservations, 1) : null,
    averageLeadTime: row.reservations ? round(row.leadTime / row.reservations, 1) : null,
    repeatGuestRate: row.reservations ? round((row.repeatReservations / row.reservations) * 100, 1) : null,
  }))));

  const revenueByLocation = addShare(sortMetricRows(groupRows(
    revenueReservations,
    (row) => row.propertyLocation,
    (row) => ({ label: row.propertyLocation, revenue: 0, reservations: 0 }),
    (bucket, row) => ({
      ...bucket,
      revenue: bucket.revenue + (row.revenue || 0),
      reservations: bucket.reservations + 1,
    }),
  ).map((row) => ({ label: row.label, value: round(row.revenue, 2), reservationCount: row.reservations }))));

  const reservationsByMonth = groupRows(
    standardReservations,
    (row) => row.monthKey,
    (row) => ({ label: row.monthKey, value: 0 }),
    (bucket) => ({ ...bucket, value: bucket.value + 1 }),
  ).sort((a, b) => a.label.localeCompare(b.label));

  const reservationsByDayOfWeek = DAYS_OF_WEEK.map((label) => ({
    label,
    value: standardReservations.filter((row) => row.dayOfWeek === label).length,
  }));

  const leadTimeDistribution = LEAD_BUCKETS.map((label) => ({
    label,
    value: standardReservations.filter((row) => row.leadBucket === label).length,
  }));

  const stayLengthDistribution = STAY_BUCKETS.map((label) => ({
    label,
    value: standardReservations.filter((row) => row.stayBucket === label).length,
  }));

  const seasonalBookingPatterns = ["Winter", "Spring", "Summer", "Fall"].map((label) => {
    const rowsForSeason = standardReservations.filter((row) => row.season === label);
    return {
      label,
      reservationCount: rowsForSeason.length,
      averageLeadTime: round(average(rowsForSeason, (row) => row.leadTime), 1),
      averageStayLength: round(average(rowsForSeason, (row) => row.nights), 1),
      revenue: round(sum(rowsForSeason.filter((row) => row.hasRevenue), (row) => row.revenue), 2),
    };
  });

  const channelPerformance = CHANNEL_ORDER.map((label) => {
    const rowsForChannel = standardReservationsWithRepeat.filter((row) => row.channel === label);
    const channelRevenueRows = rowsForChannel.filter((row) => row.hasRevenue);
    const revenue = sum(channelRevenueRows, (row) => row.revenue);
    const nights = sum(channelRevenueRows, (row) => row.nights);
    return {
      label,
      revenue: round(revenue, 2),
      reservationCount: rowsForChannel.length,
      adr: nights ? round(revenue / nights, 2) : null,
      averageStayLength: rowsForChannel.length ? round(sum(rowsForChannel, (row) => row.nights) / rowsForChannel.length, 1) : null,
      averageLeadTime: rowsForChannel.length ? round(sum(rowsForChannel, (row) => row.leadTime) / rowsForChannel.length, 1) : null,
      repeatGuestRate: rowsForChannel.length
        ? round((rowsForChannel.filter((row) => row.isRepeatGuest).length / rowsForChannel.length) * 100, 1)
        : null,
      revenueShare: percentage(revenue, sum(revenueReservationsWithRepeat, (row) => row.revenue)),
    };
  }).sort((a, b) => (b.revenue || 0) - (a.revenue || 0));

  const propertyPerformance = sortMetricRows(groupRows(
    normalized,
    (row) => row.propertyName,
    (row) => ({ label: row.propertyName, reservations: 0, cancellations: 0, revenue: 0, revenueRows: 0, nights: 0, repeatReservations: 0, leadTime: 0 }),
    (bucket, row) => ({
      ...bucket,
      reservations: bucket.reservations + 1,
      cancellations: bucket.cancellations + (row.isCancelled ? 1 : 0),
      revenue: bucket.revenue + (row.hasRevenue && !row.isCancelled ? row.revenue : 0),
      revenueRows: bucket.revenueRows + (row.hasRevenue && !row.isCancelled ? 1 : 0),
      nights: bucket.nights + (!row.isCancelled ? row.nights : 0),
      repeatReservations: bucket.repeatReservations + (!row.isCancelled && repeatGuestKeys.has(row.guestKey) ? 1 : 0),
      leadTime: bucket.leadTime + (!row.isCancelled ? row.leadTime : 0),
    }),
  ).map((row) => ({
    label: row.label,
    revenue: round(row.revenue, 2),
    reservationCount: row.reservations,
    adr: row.nights ? round(row.revenue / row.nights, 2) : null,
    averageStayLength: row.reservations - row.cancellations > 0 ? round(row.nights / (row.reservations - row.cancellations), 1) : null,
    averageLeadTime: row.reservations - row.cancellations > 0 ? round(row.leadTime / (row.reservations - row.cancellations), 1) : null,
    repeatGuestRate: row.reservations - row.cancellations > 0 ? round((row.repeatReservations / (row.reservations - row.cancellations)) * 100, 1) : null,
    cancellationRate: row.reservations > 0 ? round((row.cancellations / row.reservations) * 100, 1) : null,
  })), "revenue");

  const guestLoyaltyRows = [...guestGroups.entries()].map(([guestKey, guestRows]) => {
    const sorted = [...guestRows].sort((a, b) => moment(a.arrivalDate).valueOf() - moment(b.arrivalDate).valueOf());
    const gaps = [];
    for (let index = 1; index < sorted.length; index += 1) {
      gaps.push(moment(sorted[index].arrivalDate).diff(moment(sorted[index - 1].arrivalDate), "days"));
    }
    const revenue = sum(sorted.filter((row) => row.hasRevenue), (row) => row.revenue);
    return {
      guestKey,
      guestName: sorted[0].guestName || "Unknown Guest",
      guestEmail: sorted[0].guestEmail || "",
      stayCount: sorted.length,
      repeatStayCount: Math.max(sorted.length - 1, 0),
      revenue: round(revenue, 2),
      averageTimeBetweenVisits: round(average(gaps), 1),
      properties: [...new Set(sorted.map((row) => row.propertyName))],
      channels: [...new Set(sorted.map((row) => row.channel))],
    };
  });

  const repeatGuests = guestLoyaltyRows.filter((guest) => guest.stayCount > 1);
  const repeatRevenueByProperty = sortMetricRows(groupRows(
    revenueReservationsWithRepeat.filter((row) => row.isRepeatGuest),
    (row) => row.propertyName,
    (row) => ({ label: row.propertyName, value: 0 }),
    (bucket, row) => ({ ...bucket, value: bucket.value + (row.revenue || 0) }),
  ).map((row) => ({ label: row.label, value: round(row.value, 2) })));

  const repeatRevenueByChannel = sortMetricRows(groupRows(
    revenueReservationsWithRepeat.filter((row) => row.isRepeatGuest),
    (row) => row.channel,
    (row) => ({ label: row.channel, value: 0 }),
    (bucket, row) => ({ ...bucket, value: bucket.value + (row.revenue || 0) }),
  ).map((row) => ({ label: row.label, value: round(row.value, 2) })));

  const newVsReturningGuests = [
    { label: "New Guests", value: guestLoyaltyRows.length - repeatGuests.length },
    { label: "Returning Guests", value: repeatGuests.length },
  ];

  const totalRevenue = round(sum(revenueReservations, (row) => row.revenue), 2) || 0;
  const totalReservations = standardReservations.length;
  const totalNights = sum(standardReservations, (row) => row.nights);
  const averageReservationValue = totalReservations ? round(totalRevenue / totalReservations, 2) : null;
  const adr = totalNights ? round(totalRevenue / totalNights, 2) : null;
  const averageStayLength = totalReservations ? round(totalNights / totalReservations, 1) : null;
  const averageLeadTime = totalReservations ? round(sum(standardReservations, (row) => row.leadTime) / totalReservations, 1) : null;
  const cancellationRate = normalized.length ? round((cancelledReservations.length / normalized.length) * 100, 1) : null;
  const repeatGuestRate = totalReservations
    ? round((standardReservationsWithRepeat.filter((row) => row.isRepeatGuest).length / totalReservations) * 100, 1)
    : null;
  const repeatGuestRevenue = round(sum(revenueReservationsWithRepeat.filter((row) => row.isRepeatGuest), (row) => row.revenue), 2) || 0;
  const guestLifetimeValue = guestLoyaltyRows.length ? round(totalRevenue / guestLoyaltyRows.length, 2) : null;

  const repeatVisitIntervals = repeatGuests
    .map((guest) => guest.averageTimeBetweenVisits)
    .filter((value) => Number.isFinite(value));
  const averageTimeBetweenRepeatVisits = repeatVisitIntervals.length
    ? round(average(repeatVisitIntervals), 1)
    : null;

  const propertyMedianAdr = propertyPerformance
    .map((row) => row.adr)
    .filter((value) => Number.isFinite(value))
    .sort((a, b) => a - b);
  const medianAdr = propertyMedianAdr.length
    ? propertyMedianAdr[Math.floor(propertyMedianAdr.length / 2)]
    : null;

  const topRevenueProperty = propertyPerformance[0] || null;
  const topAdrProperty = [...propertyPerformance]
    .filter((row) => row.adr !== null)
    .sort((a, b) => (b.adr || 0) - (a.adr || 0))[0] || null;
  const topRepeatProperty = [...propertyPerformance]
    .filter((row) => row.repeatGuestRate !== null)
    .sort((a, b) => (b.repeatGuestRate || 0) - (a.repeatGuestRate || 0))[0] || null;
  const lowestPerformingProperties = [...propertyPerformance]
    .filter((row) => row.reservationCount >= 2)
    .sort((a, b) => ((a.revenue || 0) - (b.revenue || 0)))
    .slice(0, 5);

  const growthProperties = propertyPerformance.filter(
    (row) => row.repeatGuestRate !== null && row.repeatGuestRate >= 20 && medianAdr !== null && (row.adr || 0) < medianAdr,
  );

  const growthChannels = computeGrowthRows(revenueReservations, (row) => row.channel, "revenue").slice(0, 3);
  const growthPropertiesRows = computeGrowthRows(revenueReservations, (row) => row.propertyName, "revenue").slice(0, 3);
  const growthLocationsRows = computeGrowthRows(revenueReservations, (row) => row.propertyLocation, "revenue").slice(0, 3);

  const shoulderSeasonRows = standardReservations.filter((row) => ["Spring", "Fall"].includes(row.season));
  const nonDirectRepeatRevenue = sum(
    revenueReservationsWithRepeat.filter((row) => row.isRepeatGuest && row.channel !== "Direct"),
    (row) => row.revenue,
  );
  const pricingOpportunityBase = sum(
    revenueReservations.filter((row) => {
      const property = propertyPerformance.find((item) => item.label === row.propertyName);
      return property && property.adr !== null && medianAdr !== null && property.adr < medianAdr;
    }),
    (row) => row.revenue,
  );
  const longStayOpportunityBase = standardReservations.filter((row) => row.stayBucket === "3-4 Nights");
  const crossSellOpportunityBase = standardReservations.filter((row) => row.nights >= 5);

  const opportunities = sortMetricRows([
    buildOpportunity({
      name: `Convert repeat OTA guests to direct bookings`,
      impact: nonDirectRepeatRevenue * 0.08 * annualizationFactor,
      confidence: repeatGuests.length >= 5 ? "Medium" : "Low",
      reasoning: `${repeatGuests.length} repeat guests booked more than once in the selected period, and a meaningful share came through non-direct channels where commission leakage is highest.`,
      action: "Launch a post-stay direct booking offer for repeat Airbnb, Vrbo, and Booking.com guests with email capture and bounce-back incentives.",
    }),
    buildOpportunity({
      name: `Raise ADR on underpriced high-demand properties`,
      impact: pricingOpportunityBase * 0.05 * annualizationFactor,
      confidence: pricingOpportunityBase > 0 ? "Medium" : "Low",
      reasoning: "Several properties are producing bookings below the portfolio ADR median, which suggests pricing headroom without requiring more demand generation.",
      action: "Increase ADR by 5% on properties with below-median ADR and stable booking volume, then monitor conversion weekly.",
    }),
    buildOpportunity({
      name: `Promote 5+ night stays from 3-4 night demand`,
      impact: sum(longStayOpportunityBase, (row) => row.adr || 0) * 0.5 * annualizationFactor,
      confidence: longStayOpportunityBase.length >= 10 ? "Medium" : "Low",
      reasoning: `${longStayOpportunityBase.length} reservations already land in the 3-4 night range, making them the most realistic pool to extend into higher-value stays.`,
      action: "Test midweek extension offers and 5+ night discounts on 3-4 night bookings, especially in shoulder periods.",
    }),
    buildOpportunity({
      name: `Retain more first-time guests`,
      impact: (totalRevenue - repeatGuestRevenue) * 0.03 * annualizationFactor,
      confidence: guestLoyaltyRows.length >= 20 ? "Medium" : "Low",
      reasoning: "Most guest revenue still comes from one-time stays, so even a small lift in return bookings compounds materially over a year.",
      action: "Build a 30-day and 180-day reactivation sequence for first-time guests with property-specific repeat offers.",
    }),
    buildOpportunity({
      name: `Cross-sell longer stays`,
      impact: crossSellOpportunityBase.length * 25 * annualizationFactor,
      confidence: crossSellOpportunityBase.length >= 10 ? "Medium" : "Low",
      reasoning: `${crossSellOpportunityBase.length} stays already run 5+ nights, which is the cleanest segment for extras, add-ons, and concierge upsells.`,
      action: "Bundle cleaning refreshes, late checkout, and local add-ons into pre-arrival offers for 5+ night reservations.",
    }),
    buildOpportunity({
      name: `Defend shoulder-season revenue`,
      impact: sum(shoulderSeasonRows.filter((row) => row.hasRevenue), (row) => row.revenue) * 0.06 * annualizationFactor,
      confidence: shoulderSeasonRows.length >= 8 ? "Medium" : "Low",
      reasoning: "Shoulder-season demand usually has the most elasticity; converting incremental stays here is cheaper than chasing peak-season growth.",
      action: "Promote flexible length-of-stay offers and local-event packages during Spring and Fall windows with softer occupancy patterns.",
    }),
  ], "estimatedAnnualImpact");

  const topRevenueMonth = [...revenueByMonth].sort((a, b) => (b.value || 0) - (a.value || 0))[0] || null;
  const bottomRevenueMonth = [...revenueByMonth].sort((a, b) => (a.value || 0) - (b.value || 0))[0] || null;
  const topChannel = channelPerformance[0] || null;
  const topLoyaltyChannel = [...channelPerformance]
    .filter((row) => row.reservationCount > 0)
    .sort((a, b) => (b.repeatGuestRate || 0) - (a.repeatGuestRate || 0))[0] || null;
  const longestStayChannel = [...channelPerformance]
    .filter((row) => row.reservationCount > 0)
    .sort((a, b) => (b.averageStayLength || 0) - (a.averageStayLength || 0))[0] || null;
  const mostDependentChannel = [...channelPerformance]
    .filter((row) => row.revenueShare !== null)
    .sort((a, b) => (b.revenueShare || 0) - (a.revenueShare || 0))[0] || null;

  const revenueConcentrationRisk = revenueByProperty[0]?.share >= 40 || revenueByChannel[0]?.share >= 45;
  const sourceCoverageRate = normalized.length ? round((normalized.filter((row) => row.rawSource).length / normalized.length) * 100, 1) : 0;
  const revenueCoverageRate = standardReservations.length ? round((revenueReservations.length / standardReservations.length) * 100, 1) : 0;
  const locationCoverageRate = normalized.length ? round((normalized.filter((row) => row.propertyLocation && row.propertyLocation !== "Unknown").length / normalized.length) * 100, 1) : 0;

  const payload = {
    payloadVersion: "1.0",
    metadata: {
      startDate,
      endDate,
      generatedAt: moment().toISOString(),
      daysInRange,
      totalReservations: normalized.length,
      standardReservations: totalReservations,
      cancelledReservations: cancelledReservations.length,
      revenueCoverageRate,
      sourceCoverageRate,
      locationCoverageRate,
    },
    assumptions: {
      revenueMetricsUseOnlyReservationsWithRentTotal: true,
      bookingSourceMissingValuesAreBucketedAsOther: true,
      repeatGuestIdentityOrder: ["email", "formatted_phone", "guest_name"],
      locationCityFallsBackToDestinationWhenRentalAddressCannotBeParsed: true,
      opportunityImpactsAreAnnualizedFromSelectedDateRange: true,
    },
    dataQuality: {
      revenueReservations: revenueReservations.length,
      sourceMappedReservations: normalized.filter((row) => row.rawSource).length,
      locationMappedReservations: normalized.filter((row) => row.propertyLocation && row.propertyLocation !== "Unknown").length,
      notes: [
        "Revenue metrics exclude reservations where rent_total is unavailable.",
        "Channel metrics use booking source mapping when present and fall back to pattern-based normalization.",
        "Location uses parsed rental address city when available and destination name as a fallback.",
      ],
    },
    reports: {
      revenueTrends: {
        purpose: "Show how revenue is changing over time.",
        kpis: {
          totalRevenue,
          totalReservations,
          averageReservationValue,
          adr,
          averageStayLength,
        },
        breakdowns: {
          revenueByMonth,
          revenueByYear,
          revenueByProperty,
          revenueByBookingSource: revenueByChannel,
          revenueByLocation,
        },
        findings: {
          highestRevenuePeriods: topRevenueMonth ? [topRevenueMonth] : [],
          lowestRevenuePeriods: bottomRevenueMonth ? [bottomRevenueMonth] : [],
          fastestGrowingRevenueSegments: {
            properties: growthPropertiesRows,
            channels: growthChannels,
            locations: growthLocationsRows,
          },
          revenueConcentrationRisks: {
            topPropertyShare: revenueByProperty[0]?.share || null,
            topChannelShare: revenueByChannel[0]?.share || null,
            flagged: revenueConcentrationRisk,
          },
        },
        insights: [
          topRevenueMonth
            ? `${topRevenueMonth.label} was the strongest revenue month and contributed ${round(topRevenueMonth.share, 1)}% of revenue in the selected window.`
            : "Revenue was not available for the selected period.",
          bottomRevenueMonth
            ? `${bottomRevenueMonth.label} was the weakest revenue month, which points to a clear low-season trough to defend with pricing and demand campaigns.`
            : "Low-season revenue patterns could not be ranked because monthly revenue coverage is incomplete.",
          revenueByProperty[0]
            ? `${revenueByProperty[0].label} is the largest single property revenue driver at ${round(revenueByProperty[0].share, 1)}% of tracked revenue.`
            : "Property-level revenue concentration could not be determined.",
          revenueByChannel[0]
            ? `${revenueByChannel[0].label} is the leading revenue channel, which should shape both acquisition and channel-risk decisions.`
            : "Channel revenue leadership is inconclusive because source coverage is limited.",
          revenueConcentrationRisk
            ? "Revenue is concentrated enough to create portfolio risk; management should reduce dependence on the top property or top channel before growth slows." 
            : "Revenue is reasonably diversified across properties and channels, which lowers concentration risk relative to a single-driver portfolio.",
        ],
      },
      bookingTrends: {
        purpose: "Understand how guests book.",
        kpis: {
          totalReservations,
          averageLeadTime,
          averageStayLength,
          cancellationRate,
        },
        breakdowns: {
          reservationsByMonth,
          reservationsByDayOfWeek,
          leadTimeDistribution,
          stayLengthDistribution,
          seasonalBookingPatterns,
        },
        insights: [
          leadTimeDistribution.sort((a, b) => (b.value || 0) - (a.value || 0))[0]
            ? `${leadTimeDistribution.sort((a, b) => (b.value || 0) - (a.value || 0))[0].label} is the dominant booking window, which should anchor campaign timing and staffing expectations.`
            : "Lead-time distribution could not be ranked.",
          stayLengthDistribution.sort((a, b) => (b.value || 0) - (a.value || 0))[0]
            ? `${stayLengthDistribution.sort((a, b) => (b.value || 0) - (a.value || 0))[0].label} is the most common stay pattern, so packaging and pricing should be built around that trip length.`
            : "Stay-length distribution is unavailable.",
          reservationsByDayOfWeek.sort((a, b) => (b.value || 0) - (a.value || 0))[0]
            ? `${reservationsByDayOfWeek.sort((a, b) => (b.value || 0) - (a.value || 0))[0].label} drives the highest arrival volume, which matters for turnover planning.`
            : "Arrival day-of-week demand is unclear.",
          cancellationRate !== null
            ? `Cancellation rate is ${cancellationRate}%, which should be monitored alongside channel mix and lead-time shifts.`
            : "Cancellation rate could not be calculated.",
          seasonalBookingPatterns.sort((a, b) => (b.reservationCount || 0) - (a.reservationCount || 0))[0]
            ? `${seasonalBookingPatterns.sort((a, b) => (b.reservationCount || 0) - (a.reservationCount || 0))[0].label} is the busiest season in the selected window, so revenue management should be tuned to that demand peak.`
            : "Seasonality is not yet visible in the selected range.",
        ],
      },
      channelPerformance: {
        purpose: "Determine which booking channels perform best.",
        kpis: {
          revenueByChannel: revenueByChannel,
          reservationsByChannel: channelPerformance.map((row) => ({ label: row.label, value: row.reservationCount })),
          adrByChannel: channelPerformance.map((row) => ({ label: row.label, value: row.adr })),
          averageStayByChannel: channelPerformance.map((row) => ({ label: row.label, value: row.averageStayLength })),
          repeatGuestRateByChannel: channelPerformance.map((row) => ({ label: row.label, value: row.repeatGuestRate })),
        },
        channels: channelPerformance,
        rankings: {
          strongestToWeakest: channelPerformance,
          bestRevenueChannel: topChannel,
          bestLoyaltyChannel: topLoyaltyChannel,
          bestStayLengthChannel: longestStayChannel,
          mostDependentChannel,
        },
        insights: [
          topChannel ? `${topChannel.label} is the strongest revenue channel in this window.` : "No revenue-leading channel could be identified.",
          topLoyaltyChannel ? `${topLoyaltyChannel.label} has the best repeat guest performance, making it the strongest loyalty channel.` : "Loyalty leadership by channel is inconclusive.",
          longestStayChannel ? `${longestStayChannel.label} produces the longest stays, which makes it the best target for cross-sell and ancillary revenue.` : "Stay-length leadership by channel is unclear.",
          mostDependentChannel ? `${round(mostDependentChannel.revenueShare, 1)}% of tracked revenue depends on ${mostDependentChannel.label}, which creates channel concentration risk.` : "Channel dependence is currently diversified.",
          sourceCoverageRate < 70
            ? `Only ${sourceCoverageRate}% of reservations include a source value, so channel conclusions should be treated as directional rather than complete.`
            : "Booking source coverage is strong enough for channel decisions with moderate confidence.",
        ],
      },
      propertyPerformance: {
        purpose: "Compare properties.",
        kpis: {
          revenue: propertyPerformance.map((row) => ({ label: row.label, value: row.revenue })),
          reservations: propertyPerformance.map((row) => ({ label: row.label, value: row.reservationCount })),
          adr: propertyPerformance.map((row) => ({ label: row.label, value: row.adr })),
          averageStayLength: propertyPerformance.map((row) => ({ label: row.label, value: row.averageStayLength })),
          repeatGuestRate: propertyPerformance.map((row) => ({ label: row.label, value: row.repeatGuestRate })),
          cancellationRate: propertyPerformance.map((row) => ({ label: row.label, value: row.cancellationRate })),
        },
        properties: propertyPerformance,
        rankings: {
          topRevenueProperties: propertyPerformance.slice(0, 5),
          topAdrProperties: [...propertyPerformance].filter((row) => row.adr !== null).sort((a, b) => (b.adr || 0) - (a.adr || 0)).slice(0, 5),
          topRepeatGuestProperties: [...propertyPerformance].filter((row) => row.repeatGuestRate !== null).sort((a, b) => (b.repeatGuestRate || 0) - (a.repeatGuestRate || 0)).slice(0, 5),
          lowestPerformingProperties,
        },
        findings: {
          underperformingProperties: lowestPerformingProperties,
          overperformingProperties: propertyPerformance.slice(0, 3),
          growthPotentialProperties: growthProperties.slice(0, 5),
        },
        insights: [
          topRevenueProperty ? `${topRevenueProperty.label} is the best performing property by revenue.` : "Top property revenue driver could not be identified.",
          topAdrProperty ? `${topAdrProperty.label} leads the portfolio on ADR, which makes it the pricing benchmark.` : "ADR leadership is inconclusive.",
          topRepeatProperty ? `${topRepeatProperty.label} has the highest repeat guest rate, which signals stronger guest-product fit than the rest of the portfolio.` : "Repeat-guest leaders are not clear yet.",
          lowestPerformingProperties[0] ? `${lowestPerformingProperties[0].label} is the weakest tracked property and should be reviewed for pricing, merchandising, or operations friction.` : "No clear underperformer stands out in the selected period.",
          growthProperties[0] ? `${growthProperties[0].label} looks underpriced relative to its loyalty performance and deserves a pricing test.` : "Growth-potential properties are not distinct enough to call out confidently.",
        ],
      },
      repeatGuests: {
        purpose: "Measure guest loyalty.",
        kpis: {
          repeatGuestRate,
          repeatGuestRevenue,
          averageTimeBetweenVisits: averageTimeBetweenRepeatVisits,
          guestLifetimeValue,
        },
        breakdowns: {
          newVsReturningGuests,
          repeatRevenueByProperty,
          repeatRevenueByChannel,
          topReturningGuests: repeatGuests.sort((a, b) => (b.revenue || 0) - (a.revenue || 0)).slice(0, 10),
          propertiesGeneratingHighestLoyalty: [...propertyPerformance].filter((row) => row.repeatGuestRate !== null).sort((a, b) => (b.repeatGuestRate || 0) - (a.repeatGuestRate || 0)).slice(0, 5),
        },
        insights: [
          repeatGuestRate !== null ? `${repeatGuestRate}% of standard reservations came from repeat guests within the selected data window.` : "Repeat guest rate could not be calculated.",
          repeatGuestRevenue > 0 ? `${round(percentage(repeatGuestRevenue, totalRevenue), 1)}% of tracked revenue came from repeat guests, which quantifies how much growth depends on loyalty versus acquisition.` : "Repeat revenue is not visible because revenue coverage is limited.",
          averageTimeBetweenRepeatVisits !== null ? `Returning guests come back after an average of ${averageTimeBetweenRepeatVisits} days, which is the right cadence for reactivation campaigns.` : "Average time between repeat stays is not yet available.",
          repeatRevenueByProperty[0] ? `${repeatRevenueByProperty[0].label} generates the most repeat revenue and should inform portfolio-wide guest experience standards.` : "No property clearly leads repeat revenue.",
          repeatRevenueByChannel[0] ? `${repeatRevenueByChannel[0].label} is the strongest repeat-revenue channel, which makes it the best feeder pool for direct conversion.` : "No channel clearly leads repeat revenue.",
        ],
      },
      revenueOpportunities: {
        purpose: "Identify opportunities to increase revenue.",
        opportunities,
      },
    },
  };

  payload.executiveSummary = {
    topFindings: [
      topChannel ? `${topChannel.label} is the biggest revenue channel driver.` : "Revenue channel leadership is unclear.",
      revenueConcentrationRisk ? "Revenue concentration is the biggest portfolio risk in the current mix." : "Revenue concentration risk is currently manageable.",
      topRevenueProperty ? `${topRevenueProperty.label} is the best performing property by revenue.` : "No single best property could be confirmed.",
      topChannel ? `${topChannel.label} is also the best performing channel on absolute revenue.` : "Best channel performance is inconclusive.",
      repeatGuestRate !== null ? `Guest loyalty is contributing ${repeatGuestRate}% of standard reservations.` : "Guest loyalty could not be quantified confidently.",
      opportunities[0] ? `${opportunities[0].opportunityName} is the largest near-term revenue opportunity.` : "No revenue opportunity could be quantified yet.",
      averageLeadTime !== null ? `Average lead time is ${averageLeadTime} days, which should guide booking window marketing.` : "Lead time is not currently measurable.",
      cancellationRate !== null ? `Cancellation rate sits at ${cancellationRate}%, which should be benchmarked by channel.` : "Cancellation rate is unavailable.",
      revenueCoverageRate < 70 ? `Only ${revenueCoverageRate}% of reservations include revenue, so finance conclusions should be treated as directional.` : "Revenue coverage is sufficient for decision support.",
      sourceCoverageRate < 70 ? `Booking source coverage is only ${sourceCoverageRate}%, so channel reporting needs better PMS source hygiene.` : "Booking source coverage is strong enough for channel steering.",
    ],
    biggestRevenueDriver: topChannel,
    biggestRisk: revenueConcentrationRisk
      ? "Revenue concentration in the top property/channel"
      : "Inconsistent PMS revenue and source coverage",
    bestPerformingProperty: topRevenueProperty,
    bestPerformingChannel: topChannel,
    guestLoyaltyFindings: {
      repeatGuestRate,
      repeatGuestRevenue,
      topRepeatProperty,
      topLoyaltyChannel,
    },
    revenueOpportunityFindings: opportunities.slice(0, 3),
    recommendedManagementActions: opportunities.slice(0, 5).map((item) => item.recommendedAction),
  };

  return payload;
}

export function formatDisplayValue(value, type = "number") {
  if (value === null || value === undefined) return "N/A";
  if (type === "currency") return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(value);
  if (type === "percent") return `${round(value, 1)}%`;
  if (type === "days") return `${round(value, 1)} days`;
  if (type === "nights") return `${round(value, 1)} nights`;
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 1 }).format(value);
}